import {
    Grid,
    Dialog,
    DialogTitle,
    DialogActions,
    DialogContent,
    Button,
    DialogContentText,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Typography,
    Alert,
    AlertTitle,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { green, red } from '@mui/material/colors';
import * as React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type StrategicVolumeUploadResult = {
    sheet: string;
    errors: { error: string; rows: number[] }[];
    totalRows: number;
    totalReports: number;
    uploadContainsInactiveVolumes: boolean;
};

type StrategicVolumeUploadResultsDialogProps = {
    open: boolean;
    results: StrategicVolumeUploadResult[];
    onClose: () => void;
};

const StrategicVolumeUploadResultsDialog = ({ open, results, onClose }: StrategicVolumeUploadResultsDialogProps) => {
    const { t } = useTranslation('bidding');
    const [expandedSheets, setExpandedSheets] = React.useState<string[]>([]);

    const sheetsWithErrors = useMemo(() => results.filter((res) => res.errors.length > 0), [results]);
    const errorsCount = useMemo(
        () => sheetsWithErrors.reduce((acc, res) => acc + res.errors.length, 0),
        [sheetsWithErrors]
    );

    useEffect(() => {
        setExpandedSheets(sheetsWithErrors.map((res) => res.sheet));
    }, [sheetsWithErrors]);

    const handleResultChange = useCallback(
        (result: StrategicVolumeUploadResult) => {
            if (result.errors.length === 0) {
                return;
            }

            if (expandedSheets.includes(result.sheet)) {
                setExpandedSheets(expandedSheets.filter((sheet) => sheet !== result.sheet));
            } else {
                setExpandedSheets([...expandedSheets, result.sheet]);
            }
        },
        [expandedSheets]
    );

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{t('uploadResults.title')}</DialogTitle>
            <DialogContent>
                <DialogContentText>{t('uploadResults.sheetsProcessed', { count: results.length })}</DialogContentText>
                {sheetsWithErrors.length > 0 && (
                    <DialogContentText sx={{ color: red['600'] }}>
                        {t('uploadResults.sheetsHaveErrors', { sheets: sheetsWithErrors.length, errors: errorsCount })}
                    </DialogContentText>
                )}
                <Grid item xs={12} mt={1}>
                    {results.map((result, index) => (
                        <Accordion
                            expanded={expandedSheets.includes(result.sheet)}
                            onChange={() => handleResultChange(result)}
                            key={index}
                        >
                            <AccordionSummary
                                expandIcon={result.errors.length ? <ExpandMoreIcon /> : false}
                                aria-controls={`sheet${index}-content`}
                                id={`sheet${index}-header`}
                                sx={{ cursor: result.errors.length ? 'pointer' : 'default !important' }}
                            >
                                <Grid container spacing={1}>
                                    <Grid item xs={8}>
                                        <Typography component="div" sx={{ fontWeight: 'bold' }}>
                                            {result.sheet}
                                        </Typography>
                                        <Typography component="div" sx={{ color: 'text.secondary' }}>
                                            {t('uploadResults.rowCount', { count: result.totalRows })}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4} textAlign={'right'}>
                                        <Typography component="div">
                                            {t('uploadResults.reportCount', { count: result.totalReports })}
                                        </Typography>
                                        <Typography
                                            component="div"
                                            sx={{ color: result.errors.length ? red['600'] : green['600'] }}
                                        >
                                            {t('uploadResults.errorCount', { count: result.errors.length })}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </AccordionSummary>
                            <AccordionDetails sx={{ paddingTop: 0 }}>
                                {result.uploadContainsInactiveVolumes && (
                                    <Alert severity="warning">{t('uploadResults.inactiveVolumes')}</Alert>
                                )}
                                {result.errors.map((error, eIndex) => (
                                    <Alert
                                        key={eIndex}
                                        severity="error"
                                        variant="outlined"
                                        sx={{ marginTop: 1 }}
                                        icon={false}
                                    >
                                        <AlertTitle sx={{ fontWeight: 600, fontSize: 13 }}>{error.error}</AlertTitle>
                                        <Typography sx={{ fontSize: 13 }}>
                                            {t('uploadResults.affectedRows', { rows: error.rows.join(', ') })}
                                        </Typography>
                                    </Alert>
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('action.close')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default StrategicVolumeUploadResultsDialog;
